/**
 * blockModel.ts — the block ⇄ tree converter for the coresignal-lab-style block editor.
 *
 * The `Group/Filter` tree stays the source of truth (count/search/rail/saved-searches all read it).
 * The block editor is a faithful VIEW over it: semantic cards (Work Experience / Education / Skills /
 * Languages / person-field / raw group) ↔ our engine AST. A Work Experience block = a `scope:'experience'`
 * group whose leaves share a `when`; Education = `scope:'education'`; everything else = root leaves.
 *
 * Mapping rules follow the verified port spec: `when` on experience leaves comes from the block's WHEN
 * pill; skills fan out (all → one leaf per value, any → one multi-value leaf); company-scoped size and
 * other person-derived fields never carry `when`; scope-less OR/NOT groups stay raw (advanced) blocks.
 */

import type { QueryNode, Group, Filter } from '@/lib/talentPool.api';
import { isGroup } from '@/lib/talentPool.api';
import { gid } from '../queryTree';
import { ALL_FIELD_OPTS, fieldLabel, newLeaf, EXPERIENCE_WHEN } from '../leafFields';

export type WhenState = 'current' | 'past' | 'any';

export type Block =
    // experience rows each carry their OWN `when` (the WHEN pill bulk-sets them); never split a group by when
    | { kind: 'experience'; id: string; when: WhenState; matchAll: boolean; exclude: boolean; rows: Filter[] }
    | { kind: 'education'; id: string; matchAll: boolean; exclude: boolean; rows: Filter[] }
    | { kind: 'skills'; id: string; values: string[]; similarity: number; matchType: 'all' | 'any'; negate: boolean; when?: WhenState }
    | { kind: 'languages'; id: string; langs: { language: string; proficiency?: string }[] }
    | { kind: 'field'; id: string; leaf: Filter }
    | { kind: 'raw'; id: string; group: Group };

// ── field registries (which fields each block kind offers) ──
export const EXPERIENCE_ROW_FIELDS = ['title', 'seniority', 'is_manager', 'department', 'company', 'industry', 'responsibilities'];
export const EDUCATION_ROW_FIELDS = ['field_of_study', 'degree', 'study_level', 'university', 'education_country', 'grad_year'];
// person-level fields offered as their own standalone card (skills + languages have dedicated blocks)
export const PERSON_FIELD_BLOCKS = [
    'experience_years', 'avg_tenure', 'current_role', 'num_jobs',
    'seniority', 'is_manager', 'location_country', 'location_city', 'study_level',
    'company_size', 'job_hopper', 'work_authorized_country', 'needs_visa_country',
    'certification', 'salary', 'notice_period', 'summary', 'name',
];

const KNOWN_FIELDS = new Set(ALL_FIELD_OPTS.map((o) => o.value));
export { fieldLabel };

const hasValue = (f: Filter): boolean => !!(f.values && f.values.length) || f.gte != null || f.lte != null;

/** Strip undefined/empty props so leaves stay minimal + stable (comparable JSON for count keys). */
function cleanLeaf(f: Filter): Filter {
    const out: Filter = { field: f.field };
    if (f.values && f.values.length) out.values = f.values;
    if (f.similarity != null) out.similarity = f.similarity;
    if (f.matchType) out.matchType = f.matchType;
    if (f.negate) out.negate = true;
    if (f.gte != null) out.gte = f.gte;
    if (f.lte != null) out.lte = f.lte;
    if (f.exactLevels) out.exactLevels = true;
    if (f.when && f.when !== 'any') out.when = f.when;
    if (f.proficiency && f.proficiency !== 'any') out.proficiency = f.proficiency;
    return out;
}

// ── block-kind constructors (for the Add menu) ──
export const newExperienceBlock = (): Block => ({ kind: 'experience', id: gid(), when: 'any', matchAll: true, exclude: false, rows: [newLeaf('title')] });
export const newEducationBlock = (): Block => ({ kind: 'education', id: gid(), matchAll: true, exclude: false, rows: [newLeaf('field_of_study')] });
export const newSkillsBlock = (): Block => ({ kind: 'skills', id: gid(), values: [], similarity: 0.4, matchType: 'all', negate: false });
export const newLanguagesBlock = (): Block => ({ kind: 'languages', id: gid(), langs: [] });
export const newFieldBlock = (field: string): Block => ({ kind: 'field', id: gid(), leaf: newLeaf(field) });

// ── block → tree (compile) ──
function experienceGroup(b: Extract<Block, { kind: 'experience' }>): Group | null {
    // each row keeps its OWN `when` (only meaningful on EXPERIENCE_WHEN fields) — NOT the block's, so a
    // mixed-when group (the planner's default shape) round-trips faithfully instead of being over-stamped.
    const rows = b.rows.filter(hasValue).map((r) => cleanLeaf(EXPERIENCE_WHEN.has(r.field) ? r : { ...r, when: undefined }));
    if (!rows.length) return null;
    return { type: 'group', combinator: b.matchAll ? 'and' : 'or', ...(b.exclude ? { negate: true } : {}), scope: 'experience', children: rows };
}
function educationGroup(b: Extract<Block, { kind: 'education' }>): Group | null {
    const rows = b.rows.filter(hasValue).map((r) => cleanLeaf({ ...r, when: undefined }));
    if (!rows.length) return null;
    return { type: 'group', combinator: b.matchAll ? 'and' : 'or', ...(b.exclude ? { negate: true } : {}), scope: 'education', children: rows };
}

export function blocksToTree(blocks: Block[], rootCombinator: 'and' | 'or', rootNegate: boolean): Group {
    const children: QueryNode[] = [];
    for (const b of blocks) {
        if (b.kind === 'experience') {
            const g = experienceGroup(b);
            if (g) children.push(g);
        } else if (b.kind === 'education') {
            const g = educationGroup(b);
            if (g) children.push(g);
        } else if (b.kind === 'skills') {
            const vals = b.values.filter(Boolean);
            if (!vals.length) continue;
            const extra = { ...(b.negate ? { negate: true } : {}), ...(b.when && b.when !== 'any' ? { when: b.when } : {}) };
            if (b.matchType === 'all') vals.forEach((v) => children.push(cleanLeaf({ field: 'skill', values: [v], similarity: b.similarity, ...extra })));
            else children.push(cleanLeaf({ field: 'skill', values: vals, similarity: b.similarity, matchType: 'any', ...extra }));
        } else if (b.kind === 'languages') {
            b.langs.forEach((l) => {
                if (l.language) children.push(cleanLeaf({ field: 'language', values: [l.language], ...(l.proficiency && l.proficiency !== 'any' ? { proficiency: l.proficiency } : {}) }));
            });
        } else if (b.kind === 'field') {
            if (hasValue(b.leaf)) children.push(cleanLeaf(b.leaf));
        } else if (b.kind === 'raw') {
            if (b.group.children.length) children.push(b.group);
        }
    }
    return { type: 'group', combinator: rootCombinator, ...(rootNegate ? { negate: true } : {}), children };
}

// ── tree → blocks (parse) ──

/** ONE scope group → ONE block; leaves keep their own `when` (never split — that breaks same-entry co-occurrence). */
function experienceBlockFromGroup(g: Group): Block {
    const rows = g.children.filter((c): c is Filter => !isGroup(c)).map((f) => ({ ...f }));
    // pill display = a representative `when` (first non-any); rows keep their individual whens verbatim.
    const when = (rows.find((r) => r.when && r.when !== 'any')?.when as WhenState) ?? 'any';
    return { kind: 'experience', id: gid(), when, matchAll: g.combinator !== 'or', exclude: !!g.negate, rows };
}

/** Root skill leaves → blocks, bucketed by (negate, when) so exclusions/temporal stay separate; within a
 *  bucket, single-value/'all' leaves merge into one 'all' block, each multi-value 'any' leaf keeps its own. */
function skillsBlocksFromLeaves(leaves: Filter[]): Block[] {
    const buckets = new Map<string, Filter[]>();
    for (const l of leaves) {
        const key = `${l.negate ? 1 : 0}|${l.when ?? 'any'}`;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key)!.push(l);
    }
    const out: Block[] = [];
    for (const [key, ls] of buckets) {
        const negate = key.startsWith('1');
        const when = key.split('|')[1] as WhenState;
        const mk = (values: string[], similarity: number, matchType: 'all' | 'any'): Block => ({ kind: 'skills', id: gid(), values, similarity, matchType, negate, ...(when !== 'any' ? { when } : {}) });
        ls.filter((l) => l.matchType === 'any').forEach((a) => out.push(mk(a.values ?? [], a.similarity ?? 0.4, 'any')));
        const allLeaves = ls.filter((l) => l.matchType !== 'any');
        if (allLeaves.length) out.push(mk(allLeaves.flatMap((l) => l.values ?? []), allLeaves[0].similarity ?? 0.4, 'all'));
    }
    return out;
}

export function treeToBlocks(root: Group): { blocks: Block[]; rootCombinator: 'and' | 'or'; rootNegate: boolean; dropped: number } {
    const blocks: Block[] = [];
    const skillLeaves: Filter[] = [];
    const langLeaves: Filter[] = [];
    let dropped = 0;

    for (const child of root.children) {
        if (isGroup(child)) {
            const hasNested = child.children.some(isGroup);
            // a scope group must contain only leaves to be a semantic card; nested subgroups → keep as raw so nothing is lost
            if (child.scope === 'experience' && !hasNested) blocks.push(experienceBlockFromGroup(child));
            else if (child.scope === 'education' && !hasNested) {
                blocks.push({ kind: 'education', id: gid(), matchAll: child.combinator !== 'or', exclude: !!child.negate, rows: child.children.filter((c): c is Filter => !isGroup(c)).map((f) => ({ ...f, when: undefined })) });
            } else {
                blocks.push({ kind: 'raw', id: gid(), group: child }); // scope-less OR/NOT group, or a scope group with nested subgroups → advanced
            }
        } else {
            const f = child;
            if (f.field === 'skill') skillLeaves.push(f);
            // a single-value language leaf → a Languages row; a MULTI-value language leaf is an OR-pool
            // (e.g. alias-expansion Chinese→[Chinese,Mandarin,Cantonese]) — keep it verbatim as a field
            // card so its any-of semantics survive (the Languages card AND-joins its rows).
            else if (f.field === 'language' && (f.values?.length ?? 0) <= 1) langLeaves.push(f);
            else if (KNOWN_FIELDS.has(f.field)) blocks.push({ kind: 'field', id: gid(), leaf: f });
            else dropped++;
        }
    }
    if (skillLeaves.length) blocks.push(...skillsBlocksFromLeaves(skillLeaves));
    if (langLeaves.length) {
        // fan out multi-value language leaves so no language is dropped (one card row per language)
        const langs = langLeaves.flatMap((l) => (l.values ?? []).map((v) => ({ language: v, proficiency: l.proficiency }))).filter((x) => x.language);
        if (langs.length) blocks.push({ kind: 'languages', id: gid(), langs });
    }
    return { blocks, rootCombinator: root.combinator, rootNegate: !!root.negate, dropped };
}
